import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Search, ShieldCheck, Loader2 } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../../../lib/api";
import { useAuthStore } from "../../../store/auth-store";

/**
 * Onboarding, korak "Kompanija". Od razgovora "Firma nastaje u onboardingu"
 * (signup od sada ne pravi firmu, samo email+ime - vidi auth-store.ts
 * hasCompany) - ovaj korak ima DVA moda:
 * - hasCompany=false (uobičajen slučaj nakon confirmAccount): NEMA GET
 *   /company da se pozove (ne postoji firma) - forma kreće prazna i submit
 *   ide na POST /company/onboarding (createForOnboarding), koji vraća NOVU
 *   JWT sa companyId - čuvamo je u store-u i tek TAD nastavljamo wizard.
 * - hasCompany=true (npr. korisnik se vratio na onboarding kasnije, firma
 *   već postoji): ponaša se kao ranije - GET/PATCH /company (isti endpoint
 *   kao "Podešavanja" stranica).
 */
export function CompanyStep({ onNext }: { onNext: () => void }) {
  const hasCompany = useAuthStore((s) => s.hasCompany);
  const setHasCompany = useAuthStore((s) => s.setHasCompany);
  const setToken = useAuthStore((s) => s.login);
  const user = useAuthStore((s) => s.user);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["company-settings-onboarding"],
    queryFn: () => api.getCompanySettings(),
    enabled: hasCompany,
  });

  const [name, setName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [address, setAddress] = useState("");
  const [isVatRegistered, setIsVatRegistered] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const [lookupOk, setLookupOk] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<{ accountNumber: string; bankName: string | null }[]>([]);

  if (hasCompany && settings && !hydrated) {
    setName(settings.name);
    setTaxId(settings.taxId);
    setRegistrationNumber(settings.registrationNumber);
    setAddress(settings.businessLocationAddress ?? "");
    setIsVatRegistered(settings.isVatRegistered);
    setHydrated(true);
  }

  const lookup = useMutation({
    mutationFn: () => api.lookupCompanyByPib(taxId),
    onSuccess: (company) => {
      setName(company.name || name);
      if (company.registrationNumber) setRegistrationNumber(company.registrationNumber);
      if (company.address) setAddress(company.address);
      setBankAccounts((company.bankAccounts ?? []).map((a) => ({ accountNumber: a.accountNumber, bankName: a.bankName })));
      setLookupOk(true);
    },
  });

  const create = useMutation({
    mutationFn: () =>
      api.createCompanyOnboarding({
        companyName: name,
        taxId,
        registrationNumber,
        address,
        isVatRegistered,
        bankAccounts: bankAccounts.length ? bankAccounts : undefined,
      }),
    onSuccess: (result) => {
      // Nova JWT VEĆ ima companyId - dalji koraci (Paket/Moduli/Korisnici)
      // od sada rade preko običnih @Tenant() ruta.
      if (user) setToken(result.accessToken, user, result.companyName, true);
      setHasCompany(true, result.companyName);
      onNext();
    },
  });

  const update = useMutation({
    mutationFn: () =>
      api.updateCompanySettings({
        name,
        taxId,
        registrationNumber,
        businessLocationAddress: address,
        isVatRegistered,
      }),
    onSuccess: () => onNext(),
  });

  const save = hasCompany ? update : create;

  return (
    <div className="grid gap-6 rounded-xl border border-border bg-surface p-8 shadow-subtle md:grid-cols-[1.1fr_1.4fr]">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand">Podaci o kompaniji</p>
        <h1 className="mb-3 text-2xl font-bold text-slate-900">Recite nam nešto o vašoj kompaniji</h1>
        <p className="text-sm text-slate-500">
          Proverite osnovne podatke o vašem poslovanju. Ovi podaci se koriste za fiskalne dokumente, izveštaje i
          podešavanja sistema.
        </p>
        <div className="mt-6 rounded-lg bg-app-bg p-3 text-xs text-slate-500">
          Podaci moraju biti tačni. Kasnije ih uvek možete izmeniti u Podešavanjima.
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {hasCompany && isLoading ? (
          <p className="text-sm text-slate-400">Učitavanje...</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Poslovni naziv</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">PIB</label>
                <div className="flex gap-2">
                  <input
                    value={taxId}
                    onChange={(e) => {
                      setTaxId(e.target.value);
                      setLookupOk(false);
                    }}
                    className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => lookup.mutate()}
                    disabled={lookup.isPending || taxId.replace(/\D/g, "").length !== 9}
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-border text-slate-500 hover:bg-slate-50 disabled:opacity-40"
                    title="Pretraži po PIB-u"
                  >
                    {lookup.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </button>
                </div>
                {lookupOk && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                    <ShieldCheck className="h-3.5 w-3.5" /> Podaci su pronađeni i popunjeni
                  </p>
                )}
                {lookupOk && bankAccounts.length > 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    Tekući računi: {bankAccounts.map((a) => a.accountNumber).join(", ")}
                  </p>
                )}
                {lookup.isError && (
                  <p className="mt-1 text-xs text-red-600">
                    {lookup.error instanceof ApiError
                      ? lookup.error.response.message
                      : "Firma nije pronađena - unesite podatke ručno."}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Matični broj</label>
                <input
                  value={registrationNumber}
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                  required
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Adresa</label>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                required
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={isVatRegistered}
                onChange={(e) => setIsVatRegistered(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              Firma je u sistemu PDV-a
            </label>

            {save.isError && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {save.error instanceof ApiError ? save.error.response.message : "Čuvanje nije uspelo."}
              </p>
            )}

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? "Čuvam..." : "Nastavi →"}
              </Button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
